import { FC, ReactElement, useState } from 'react';

import { Link } from 'react-router-dom';
import { useToggle } from 'react-use';

import { Logo, Wrapper } from '../../../../components';
import { ConnectedCartSideBar, ConnectedSearchDrawer } from '../../../../connectors';
import { Customer } from '../../../../entities/@account/Customer/Customer';
import { Navigation, NavigationItem } from '../../../../entities/@navigation/Navigation/Navigation';
import { AppRoute, appRoutes } from '../../../../entities/Routing/Routing';
import { useTrans } from '../../../../hooks';
import { AccountControls, MobileNavigation } from '..';

import './MobileMenu.scss';

interface MobileMenuProps {
    isLoading: boolean;
    isAuthenticated: boolean;
    hasActiveSubNavigation: boolean;
    navigation?: Navigation;
    activeNavigation?: Navigation;
    accountNavigationItems: NavigationItem[];
    topNavbarItems: NavigationItem[];
    wishlistIsLoading: boolean;
    wishlistItemsCount: number;
    customer?: Customer;
    onNavigationChange: (navigation?: Navigation) => void;
    onBackButtonClick: () => void;
    className?: string;
}

export const MobileMenu: FC<MobileMenuProps> = ({
    isLoading,
    isAuthenticated,
    hasActiveSubNavigation,
    navigation,
    activeNavigation,
    accountNavigationItems,
    topNavbarItems,
    wishlistIsLoading,
    wishlistItemsCount,
    customer,
    onNavigationChange,
    onBackButtonClick,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const [navigationIsOpen, toggleNavigationIsOpen] = useToggle(false);
    const [shouldNavigateBack, setShouldNavigateBack] = useState<boolean>(false);
    const [isNavigatingBack, setIsNavigatingBack] = useState<boolean>(false);

    const handleCloseNavigation = (): void => {
        toggleNavigationIsOpen(false);
    };

    const handleBackButtonClick = (): void => {
        setShouldNavigateBack(true);

        // Set boolean value to prevent stuck button on fast clicks in succession
        setIsNavigatingBack(true);
    };

    const handleBackTransitionMidPoint = (): void => {
        setShouldNavigateBack(false);
        onBackButtonClick();
    };

    // Reset boolean value after transition is complete
    const handleTransitionEnd = (): void => {
        setIsNavigatingBack(false);
    };

    return (
        <div className={`mobile-menu ${className}`}>
            <Wrapper className="mobile-menu__wrapper">
                <Link
                    title={trans('containers.menu.homeLink')}
                    to={trans(appRoutes[AppRoute.home].path)}
                    onClick={handleCloseNavigation}
                >
                    <Logo className="mobile-menu__logo" />
                </Link>

                <div className="mobile-menu__controls">
                    <ConnectedSearchDrawer />

                    <AccountControls
                        wishlistIsLoading={wishlistIsLoading}
                        wishlistItemsCount={wishlistItemsCount}
                        isAuthenticated={isAuthenticated}
                        customer={customer}
                    />

                    <ConnectedCartSideBar />

                    <MobileNavigation
                        isLoading={isLoading}
                        isOpen={navigationIsOpen}
                        isNavigatingBack={isNavigatingBack}
                        hasActiveSubNavigation={hasActiveSubNavigation}
                        shouldNavigateBack={shouldNavigateBack}
                        navigation={navigation}
                        activeNavigation={activeNavigation}
                        accountNavigationItems={accountNavigationItems}
                        topNavbarItems={topNavbarItems}
                        onNavigationToggle={toggleNavigationIsOpen}
                        onNavigationChange={onNavigationChange}
                        onBackButtonClick={handleBackButtonClick}
                        onBackTransitionMidpoint={handleBackTransitionMidPoint}
                        onTransitionEnd={handleTransitionEnd}
                    />
                </div>
            </Wrapper>
        </div>
    );
};
